import React, { useState, useEffect } from 'react';
import { FileText, AlertTriangle, Shield, ClipboardCheck, Plus, Calendar, DollarSign, CheckCircle, Clock, X } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';

const CompliancePage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [violations, setViolations] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        const res = await api.get('/api/compliance/document-types');
        setDocumentTypes(res.data.data || []);
      } catch (error) {
        console.error('Erro ao carregar tipos de documentos:', error);
      }
    };
    loadDocumentTypes();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      if (activeTab === 'dashboard') {
        const dashRes = await api.get('/api/compliance/dashboard');
        setDashboard(dashRes.data.data);
      } else if (activeTab === 'documents') {
        const docsRes = await api.get('/api/compliance/documents');
        setDocuments(docsRes.data.data || []);
      } else if (activeTab === 'alerts') {
        const alertsRes = await api.get('/api/compliance/expiration-alerts', { params: { status: 'pending' } });
        setAlerts(alertsRes.data.data || []);
      } else if (activeTab === 'violations') {
        const violRes = await api.get('/api/compliance/violations');
        setViolations(violRes.data.data || []);
      } else if (activeTab === 'insurances') {
        const insRes = await api.get('/api/compliance/insurances');
        setInsurances(insRes.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'active') return 'text-green-600 bg-green-100';
    if (status === 'expired') return 'text-red-600 bg-red-100';
    if (status === 'pending_renewal') return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getSeverityColor = (severity) => {
    if (severity === 'critical') return 'text-red-600 bg-red-100';
    if (severity === 'high') return 'text-orange-600 bg-orange-100';
    if (severity === 'medium') return 'text-yellow-600 bg-yellow-100';
    return 'text-blue-600 bg-blue-100';
  };

  const handlePayViolation = async (id) => {
    try {
      await api.put(`/api/compliance/violations/${id}/pay`, {
        payment_date: new Date().toISOString().split('T')[0]
      });
      toast.success('Pagamento registrado!');
      loadData();
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      toast.error('Erro ao registrar pagamento');
    }
  };

  const loadEntities = async (entityType) => {
    if (!entityType) return;

    setLoadingEntities(true);
    try {
      if (entityType === 'vehicle') {
        const res = await api.get('/api/vehicles');
        setVehicles(res.data.data || []);
      } else if (entityType === 'driver') {
        const res = await api.get('/api/drivers');
        setDrivers(res.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar entidades:', error);
      setVehicles([]);
      setDrivers([]);
    } finally {
      setLoadingEntities(false);
    }
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Compliance e Documentação
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Gestão de documentos, multas, seguros e inspeções
          </p>
        </div>

        <button
          onClick={() => {
            setShowModal(true);
            setSelectedEntityType('');
            setVehicles([]);
            setDrivers([]);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Novo Documento
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px overflow-x-auto">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'dashboard'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <ClipboardCheck className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'documents'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Documentos
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'alerts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Alertas
            </button>
            <button
              onClick={() => setActiveTab('violations')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'violations'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <DollarSign className="w-4 h-4 inline mr-2" />
              Multas
            </button>
            <button
              onClick={() => setActiveTab('insurances')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'insurances'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              Seguros
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && dashboard && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-red-600 dark:text-red-400 font-medium">Vencidos</p>
                      <p className="text-2xl font-bold text-red-700 dark:text-red-300 mt-1">
                        {dashboard.summary.expired_documents}
                      </p>
                    </div>
                    <X className="w-6 h-6 text-red-500" />
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">Vencendo</p>
                      <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300 mt-1">
                        {dashboard.summary.expiring_soon}
                      </p>
                    </div>
                    <Clock className="w-6 h-6 text-yellow-500" />
                  </div>
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Multas</p>
                      <p className="text-2xl font-bold text-orange-700 dark:text-orange-300 mt-1">
                        {dashboard.summary.pending_violations}
                      </p>
                    </div>
                    <AlertTriangle className="w-6 h-6 text-orange-500" />
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Multas R$</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300 mt-1">
                        {dashboard.summary.pending_fines_amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    </div>
                    <DollarSign className="w-6 h-6 text-blue-500" />
                  </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-green-600 dark:text-green-400 font-medium">Seguros</p>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300 mt-1">
                        {dashboard.summary.active_insurances}
                      </p>
                    </div>
                    <Shield className="w-6 h-6 text-green-500" />
                  </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Alertas</p>
                      <p className="text-2xl font-bold text-purple-700 dark:text-purple-300 mt-1">
                        {dashboard.summary.pending_alerts}
                      </p>
                    </div>
                    <AlertTriangle className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
              </div>

              {/* Documentos por Tipo */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Status por Tipo de Documento
                </h3>
                <div className="space-y-3">
                  {dashboard.documents_by_type.map((doc) => (
                    <div key={doc.name} className="flex items-center justify-between py-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{doc.name}</p>
                        <div className="flex gap-4 mt-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Total: {doc.count}
                          </span>
                          {doc.expired > 0 && (
                            <span className="text-xs text-red-600 dark:text-red-400">
                              {doc.expired} vencido(s)
                            </span>
                          )}
                          {doc.expiring_soon > 0 && (
                            <span className="text-xs text-yellow-600 dark:text-yellow-400">
                              {doc.expiring_soon} vencendo
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Número</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Entidade</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Emissão</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Vencimento</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {doc.document_type_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {doc.document_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {doc.entity_type} - {doc.entity_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {doc.issue_date ? new Date(doc.issue_date).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {doc.expiration_date ? new Date(doc.expiration_date).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(doc.status)}`}>
                          {doc.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {documents.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Nenhum documento cadastrado</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border-l-4 border-orange-500"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {alert.document_type_name}
                        </span>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                        {alert.entity_name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Vencimento: {new Date(alert.expiration_date).toLocaleDateString('pt-BR')}
                        {alert.days_until_expiration >= 0 && (
                          <span className="ml-2">({alert.days_until_expiration} dias restantes)</span>
                        )}
                        {alert.days_until_expiration < 0 && (
                          <span className="ml-2 text-red-600 dark:text-red-400">
                            (Vencido há {Math.abs(alert.days_until_expiration)} dias)
                          </span>
                        )}
                      </p>
                    </div>
                    <AlertTriangle className="w-6 h-6 text-orange-500" />
                  </div>
                </div>
              ))}

              {alerts.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Nenhum alerta pendente</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'violations' && (
            <div className="space-y-4">
              {violations.map((violation) => (
                <div
                  key={violation.id}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          {violation.severity}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(violation.violation_date).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                        {violation.description}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Veículo: {violation.license_plate} • Motorista: {violation.driver_name || 'Não informado'}
                      </p>
                      {violation.location && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          Local: {violation.location}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        {violation.fine_amount && (
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            Valor: {formatCurrency(Number(violation.fine_amount))}
                          </span>
                        )}
                        {violation.points > 0 && (
                          <span className="text-sm text-orange-600 dark:text-orange-400">
                            {violation.points} pontos
                          </span>
                        )}
                      </div>
                    </div>
                    {violation.payment_status === 'pending' && (
                      <button
                        onClick={() => handlePayViolation(violation.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        Registrar Pagamento
                      </button>
                    )}
                    {violation.payment_status === 'paid' && (
                      <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full text-xs font-medium">
                        Pago
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {violations.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Nenhuma multa registrada</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'insurances' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {insurances.map((insurance) => (
                <div
                  key={insurance.id}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {insurance.insurance_company}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Apólice: {insurance.policy_number}
                      </p>
                    </div>
                    <Shield className="w-6 h-6 text-blue-500" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Veículo:</span> {insurance.license_plate}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Vigência:</span>{' '}
                      {new Date(insurance.start_date).toLocaleDateString('pt-BR')} até{' '}
                      {new Date(insurance.end_date).toLocaleDateString('pt-BR')}
                    </p>
                    {insurance.coverage_amount && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Cobertura:</span>{' '}
                        {insurance.coverage_amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    )}
                    {insurance.premium_amount && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Prêmio:</span>{' '}
                        {insurance.premium_amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    )}
                  </div>
                  <div className="mt-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(insurance.status)}`}>
                      {insurance.status}
                    </span>
                  </div>
                </div>
              ))}

              {insurances.length === 0 && (
                <div className="col-span-2 text-center py-12">
                  <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Nenhum seguro cadastrado</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Novo Documento */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Novo Documento
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);

              try {
                await api.post('/api/compliance/documents', {
                  document_type_id: parseInt(formData.get('document_type_id')),
                  entity_type: formData.get('entity_type'),
                  entity_id: formData.get('entity_id'),
                  document_number: formData.get('document_number'),
                  issue_date: formData.get('issue_date'),
                  expiration_date: formData.get('expiration_date'),
                  issuing_authority: formData.get('issuing_authority'),
                  notes: formData.get('notes')
                });

                toast.success('Documento cadastrado com sucesso!');
                setShowModal(false);
                loadData();
              } catch (error) {
                console.error('Erro ao cadastrar documento:', error);
                toast.error('Erro ao cadastrar documento');
              }
            }} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Documento *
                  </label>
                  <select
                    name="document_type_id"
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Selecione...</option>
                    {documentTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Entidade *
                  </label>
                  <select
                    name="entity_type"
                    required
                    value={selectedEntityType}
                    onChange={(e) => {
                      setSelectedEntityType(e.target.value);
                      loadEntities(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Selecione...</option>
                    <option value="vehicle">Veículo</option>
                    <option value="driver">Motorista</option>
                    <option value="company">Empresa</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Número do Documento *
                  </label>
                  <input
                    type="text"
                    name="document_number"
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data de Emissão *
                  </label>
                  <input
                    type="date"
                    name="issue_date"
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data de Vencimento *
                  </label>
                  <input
                    type="date"
                    name="expiration_date"
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {selectedEntityType === 'vehicle' ? 'Veículo *' :
                     selectedEntityType === 'driver' ? 'Motorista *' :
                     selectedEntityType === 'company' ? 'Empresa *' : 'Entidade *'}
                  </label>
                  {selectedEntityType === 'vehicle' ? (
                    <select
                      name="entity_id"
                      required
                      disabled={loadingEntities}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">
                        {loadingEntities ? 'Carregando...' : 'Selecione um veículo...'}
                      </option>
                      {vehicles.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.license_plate} - {v.model} {v.year}
                        </option>
                      ))}
                    </select>
                  ) : selectedEntityType === 'driver' ? (
                    <select
                      name="entity_id"
                      required
                      disabled={loadingEntities}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">
                        {loadingEntities ? 'Carregando...' : 'Selecione um motorista...'}
                      </option>
                      {drivers.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.name} - {d.cpf}
                        </option>
                      ))}
                    </select>
                  ) : selectedEntityType === 'company' ? (
                    <input
                      type="text"
                      name="entity_id"
                      required
                      placeholder="Digite o ID UUID da empresa"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  ) : (
                    <input
                      type="text"
                      name="entity_id"
                      disabled
                      placeholder="Selecione primeiro o tipo de entidade"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-400"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Órgão Emissor
                  </label>
                  <input
                    type="text"
                    name="issuing_authority"
                    placeholder="Ex: DETRAN-SP"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observações
                </label>
                <textarea
                  name="notes"
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                ></textarea>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Cadastrar Documento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompliancePage;